const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.changeLineItem = this.changeLineItem.bind(this)
    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.toggleByobDrawer = this.toggleByobDrawer.bind(this)
    this.toggleVariantOptions = this.toggleVariantOptions.bind(this)
    this.onVariantClick = this.onVariantClick.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)

    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    if (this.els?.remove?.exists) {
      this.listeners.add(
        this.els.remove.element,
        'click',
        this.removeLineItem,
      )
    }

    if (this.els?.variantToggle?.exists) {
      this.listeners.add(
        this.els.variantToggle.element,
        'click',
        this.toggleVariantOptions,
      )
    }

    if (this.els?.variantOption?.exists) {
      this.els.variantOption.elements.forEach((variantOption) => {
        this.listeners.add(
          variantOption,
          'click',
          this.onVariantClick,
        )
      })
    }

    this.listeners.add(
      document,
      'click',
      this.onDocumentClick,
    )

    this.listeners.add(
      document,
      'keydown',
      this.onDocumentKeydown,
    )

    if (this.els?.byobToggle?.exists) {
      this.listeners.add(
        this.els.byobToggle.element,
        'click',
        this.toggleByobDrawer,
      )
    }
  }

  toggleVariantOptions (event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.els?.variant?.exists) return
    if (!this.els?.variantToggle?.exists) return

    const variantElement = this.els.variant.element
    const toggleElement = this.els.variantToggle.element
    const isOpen = toggleElement.getAttribute('aria-expanded') === 'true'

    document.querySelectorAll('cart-item').forEach((cartItem) => {
      if (cartItem === this) return

      if (typeof cartItem.closeVariantOptions === 'function') {
        cartItem.closeVariantOptions()
      }
    })

    toggleElement.setAttribute('aria-expanded', (!isOpen).toString())
    variantElement.hidden = isOpen
  }

  closeVariantOptions () {
    if (!this.els?.variant?.exists) return
    if (!this.els?.variantToggle?.exists) return

    this.els.variant.element.hidden = true
    this.els.variantToggle.element.setAttribute('aria-expanded', 'false')
  }

  onVariantClick (event) {
    event.preventDefault()
    event.stopPropagation()

    const variantOption = event.currentTarget

    if (variantOption.disabled) return

    const currentVariant = this.querySelector(
      '[data-cart-item-el="variantOption"][aria-current="true"]',
    )

    if (currentVariant === variantOption) {
      this.closeVariantOptions()
      return
    }

    this.closeVariantOptions()
    this.changeLineItem(event)
  }

  onDocumentClick (event) {
    if (!this.els?.variant?.exists) return
    if (!this.els?.variantToggle?.exists) return

    const variantElement = this.els.variant.element
    const toggleElement = this.els.variantToggle.element

    const clickedInsideVariant = variantElement.contains(event.target)
    const clickedToggle = toggleElement.contains(event.target)

    if (clickedInsideVariant || clickedToggle) return

    this.closeVariantOptions()
  }

  onDocumentKeydown (event) {
    if (event.key !== 'Escape') return

    const wasOpen =
      this.els?.variantToggle?.element?.getAttribute('aria-expanded') ===
      'true'

    this.closeVariantOptions()

    if (wasOpen) {
      this.els.variantToggle.element.focus()
    }
  }

  toggleByobDrawer () {
    const drawer = this.els.byobDrawer.element

    drawer.dataset.open =
      drawer.dataset.open === 'true' ? 'false' : 'true'
  }

  // For testing
  async getCartInfo () {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()

      const cartTotal = cart.total_price
      const cartItems = cart.items

      return { cartTotal, cartItems }
    } catch (error) {
      console.error('Error fetching cart total:', error)

      return {
        cartTotal: null,
        cartItems: null,
      }
    }
  }

  async removeLineItem (event, { silent = false } = {}) {
    try {
      event?.preventDefault()

      this.loading = true

      const cartState = await window.BAO.Cart.getCart()
      const itemToRemove = cartState.items.find(
        item => item.key === this.dataset.key,
      )
      const isAutoAddedGWP =
        itemToRemove?.properties?._gwp_auto_added === 'true'
      const gwpCampaignId = itemToRemove?.properties?._gwp_campaign

      if (isAutoAddedGWP && gwpCampaignId) {
        await window.BAO.Cart.updateCartAttributes({
          [`gwp_removed_${gwpCampaignId}`]: 'true',
        })

        this.log(
          `GWP: Set cart attribute for manually removed campaign "${gwpCampaignId}"`,
        )
      }

      const state = await window.BAO.Cart.removeItem(
        this.dataset.key,
        {
          sections: this.mappedSections,
        },
      )

      this.log('removeLineItem', {
        cartInfo: await this.getCartInfo(),
        datasetKey: this.dataset.key,
        state,
        isAutoAddedGWP,
        gwpCampaignId,
      })

      if (isAutoAddedGWP && gwpCampaignId) {
        setTimeout(async () => {
          const refreshedState = await window.BAO.Cart.refreshCart()

          if (!silent && 'sections' in refreshedState) {
            this.renderSections(refreshedState.sections)
          }
        }, 100)
      }

      this.loading = false

      if (!silent) {
        if ('sections' in state && !isAutoAddedGWP) {
          this.renderSections(state.sections)
        }
      }

      return state
    } catch (error) {
      console.error('Error in removeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
      })

      this.loading = false
    }
  }

  async changeLineItem (event) {
    const variantOption = event.currentTarget

    try {
      this.loading = true

      const newVariantId = variantOption.value
      const variantElement = this.els.variant.element

      if (variantElement.dataset?.gwp === 'true') {
        const gwpDataElement = this.externalEls.gwp.element

        if (gwpDataElement) {
          gwpDataElement.dataset.variantId = newVariantId
        }
      }

      const originalKey = this.dataset.key
      const cartState = await window.BAO.Cart.getCart()
      const currentItem = cartState.items.find(
        item => item.key === originalKey,
      )
      const currentQuantity = currentItem?.quantity ?? 1
      const maxQuantity = 999
      const quantity = Math.min(currentQuantity, maxQuantity)
      const originalVariantId = currentItem?.variant_id

      if (!currentItem || !originalVariantId) {
        throw new Error('The current cart item could not be found')
      }

      this.log('changeLineItem before removing old variant', {
        cartInfo: await this.getCartInfo(),
        originalKey,
        originalVariantId,
        currentQuantity,
        currentItem,
      })

      /*
       * Only remove this exact line-item key.
       *
       * Removing every item with the same variant ID could accidentally
       * remove separate line items that have different properties.
       */
      let state = await window.BAO.Cart.cartChange({
        id: originalKey,
        quantity: 0,
        sections: this.mappedSections,
        sections_url: window.location.pathname,
      })

      this.log('changeLineItem after removing old variant', {
        originalKey,
        originalVariantId,
        stateItems: state.items.map(item => ({
          key: item.key,
          id: item.variant_id,
          qty: item.quantity,
        })),
      })

      if (variantElement.dataset?.gwp !== 'true') {
        state = await window.BAO.Cart.addItem(
          {
            id: newVariantId,
            quantity,
          },
          {
            sections: this.mappedSections,
            silent: true,
          },
        )

        const fullCart = await window.BAO.Cart.getCart()
        if (state?.sections) {
          fullCart.sections = state.sections
        }
        state = await window.BAO.Cart.processGiftWithPurchase(fullCart)
      }

      this.log('changeLineItem after adding new variant', {
        cartInfo: await this.getCartInfo(),
        stateItems: state.items.map(item => ({
          key: item.key,
          id: item.variant_id,
          qty: item.quantity,
        })),
      })

      const pushCart = this.pushCart

      if ('sections' in state) {
        this.renderSections(state.sections)
      }

      if (pushCart) {
        pushCart.loading = false
      }

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.CHANGED,
        {
          state,
        },
      )

      return state
    } catch (error) {
      console.error('Error in changeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
        newVariantId: variantOption?.value,
      })

      const pushCart = this.pushCart

      if (pushCart) {
        pushCart.loading = false
      }
    } finally {
      this.loading = false

      this.log('changeLineItem finally', {
        cartInfo: await this.getCartInfo(),
      })
    }
  }

  async onQuantityChange (event) {
    try {
      this.loading = true

      const state = await window.BAO.Cart.updateQuantity(
        this.dataset.key,
        event.detail.value,
        {
          sections: this.mappedSections,
        },
      )

      this.loading = false

      if ('sections' in state) {
        this.renderSections(state.sections)
      }
    } catch (error) {
      console.error('Error in onQuantityChange:', {
        message: error.message,
        stack: error.stack,
        quantity: event.detail.value,
        datasetKey: this.dataset.key,
        value: event.detail.value,
        sections: this.mappedSections,
      })

      this.loading = false
    }
  }

  get mappedSections () {
    return this.sections.map(section => section.section)
  }

  get loading () {
    return this._loading
  }

  set loading (value) {
    this._loading = value

    if (this.inPushCart) {
      this.pushCart.loading = value
    }

    this.setAttribute('aria-busy', value.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
